/*
 * Copyright (c) 2024 Helvia Technologies PC <contact@helvia.io>
 */

import { useCallback } from 'react';
import { useAuthenticateUserMutation } from '../../../store/auth/authApi';
import { useLocation, useNavigate } from 'react-router-dom';
import is from '../../is';
import { useDispatch } from 'react-redux';
import { IS_HRWIZ_CONSOLE_ENABLED } from '../../../constants/env';
import { INTEGRATION_TYPE, INTEGRATIONS } from '../../../constants/integrations';
import { teamsAppLogin } from '../../auth/teamsAppLogin';
import { setOrganization } from '../../../store/auth/authActions';
import { NAV_DASHBOARD } from '../../../constants/navigation';
import replaceUrlParams from '../../string/replaceUrlParams';

/**
 * Logs user in and redirects them to the appropriate location.
 */
export const useAuthenticateUserWithRedirects = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [_loginUser, mutationData] = useAuthenticateUserMutation();

  /**
   * Logs user in and redirects them to the appropriate location.
   *
   * @param {object} authData - The authentication method and data.
   * @param {string} authData.authenticationMethod - The method used to authenticate the user.
   * @param {object} authData.data - The data used to authenticate the user.
   * @param {object} authData.loginSettings - The login settings for the organization.
   */
  const loginUser = useCallback(async ({ authenticationMethod, data, loginSettings }) => {
    const inMicrosoftTeams = is.inMicrosoftTeams();
    const urlSearchString = new URLSearchParams(location.search);
    let loginResponse;

    try {
      const oidcIntegration = INTEGRATIONS.find(integration => integration.type === INTEGRATION_TYPE.OIDC_AUTHENTICATION);
      const authenticationPlugins = loginSettings?.plugins?.filter(plugin => plugin.category === oidcIntegration?.pluginCategory) || [];
      const shouldAutoLogin = loginSettings?.isSkipNativeLoginEnabled && authenticationPlugins.length === 1;
      const errorMessage = shouldAutoLogin ? { showMessage: false } : null;

      loginResponse = await _loginUser({ authenticationMethod, data, currentUrl: urlSearchString.get('next'), errorMessage }).unwrap();
    } catch (e) {
      loginResponse = { error: e?.error ?? e };
    }

    if (loginResponse?.user) {
      const redirectUrl = urlSearchString.get('next');
      const availableOrgId = loginResponse.redirectOrg;
      /*
      * If the user tried logging in to a Teams app
      * or the web version of the HRwiz console
      */
      if (inMicrosoftTeams || IS_HRWIZ_CONSOLE_ENABLED) {
        return teamsAppLogin(loginResponse, navigate, location, dispatch);
      }
      // If there's a "next" to redirect the user to after login, make sure to set the organization to the one specified
      // Otherwise, set it to the first available organization
      if (redirectUrl) {
        await dispatch(setOrganization(availableOrgId));
        navigate(redirectUrl);
      } else {
        await dispatch(setOrganization(availableOrgId, navigate));
        navigate(replaceUrlParams(NAV_DASHBOARD, { orgId: availableOrgId }));
      }
    }
    return loginResponse;
  }, [_loginUser, location, dispatch, navigate]);

  return [loginUser, mutationData];
};
